import { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Grid,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  Chip,
  TextField,
  InputAdornment,
  CircularProgress,
  Alert,
  Button,
  Pagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ListIcon from '@mui/icons-material/List';
import DeleteIcon from '@mui/icons-material/Delete';
import { useNavigate } from 'react-router-dom';
import { format, parseISO, startOfMonth, endOfMonth, eachDayOfInterval, getDay, isSameDay } from 'date-fns';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { AbsenceForm } from '../components/absences/AbsenceForm';
import { UnavailabilityTab } from '../components/absences/UnavailabilityTab';
import { ABSENCE_STATUS_CHIP_CONFIG } from '../components/absences/absenceStatusConfig';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { useAuthStore } from '@/store/authStore';
import { useToast } from '@/components/Toast';
import { getAbsences, cancelAbsence, deleteAbsenceDirect } from '@timetable/api';
import { queryKeys } from '../lib/queryKeys';
import type { AbsenceWithStatus } from '@timetable/types';

type LeftPanelTab = 'absence' | 'unavailability';
type LogView = 'list' | 'calendar';
type SortOption = 'start_date_desc' | 'start_date_asc' | 'teacher_name';

const PAGE_SIZE = 20;
const SORT_OPTIONS: { value: SortOption; label: string }[] = [
  { value: 'start_date_desc', label: 'Newest First' },
  { value: 'start_date_asc',  label: 'Oldest First' },
  { value: 'teacher_name',    label: 'Teacher A–Z'  },
];

// ─── Mini Calendar ────────────────────────────────────────────────────────────

function MiniCalendar({
  absences,
  onDayClick,
}: {
  absences: AbsenceWithStatus[];
  onDayClick: (date: string, teachers: AbsenceWithStatus[]) => void;
}) {
  const [viewDate, setViewDate] = useState(new Date());
  const [selectedDay, setSelectedDay] = useState<string | null>(null);

  const monthStart = startOfMonth(viewDate);
  const monthEnd = endOfMonth(viewDate);
  const days = eachDayOfInterval({ start: monthStart, end: monthEnd });

  // Pad start so week starts on Sunday
  const startPad = getDay(monthStart);

  function absencesOnDay(date: Date): AbsenceWithStatus[] {
    const iso = format(date, 'yyyy-MM-dd');
    return absences.filter(
      (a) => a.start_date <= iso && a.end_date >= iso && a.status !== 'cancelled'
    );
  }

  function handleDayClick(date: Date) {
    const iso = format(date, 'yyyy-MM-dd');
    const teachers = absencesOnDay(date);
    setSelectedDay(iso);
    onDayClick(iso, teachers);
  }

  const prevMonth = () => setViewDate((d) => new Date(d.getFullYear(), d.getMonth() - 1, 1));
  const nextMonth = () => setViewDate((d) => new Date(d.getFullYear(), d.getMonth() + 1, 1));

  return (
    <Box>
      {/* Month navigation */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Button size="small" onClick={prevMonth}>‹</Button>
        <Typography variant="subtitle2" fontWeight="bold">
          {format(viewDate, 'MMMM yyyy')}
        </Typography>
        <Button size="small" onClick={nextMonth}>›</Button>
      </Box>

      {/* Day-of-week headers */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.25, mb: 0.5 }}>
        {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((d) => (
          <Typography key={d} variant="caption" align="center" color="text.secondary" fontWeight="bold">
            {d}
          </Typography>
        ))}
      </Box>

      {/* Calendar grid */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.25 }}>
        {/* Leading empty cells */}
        {Array.from({ length: startPad }).map((_, i) => (
          <Box key={`pad-${i}`} />
        ))}

        {days.map((day) => {
          const iso = format(day, 'yyyy-MM-dd');
          const count = absencesOnDay(day).length;
          const isSelected = selectedDay === iso;
          const isToday = isSameDay(day, new Date());

          return (
            <Box
              key={iso}
              onClick={() => handleDayClick(day)}
              sx={{
                aspectRatio: '1',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: 1,
                cursor: 'pointer',
                border: '1px solid',
                borderColor: isSelected ? 'primary.main' : isToday ? 'primary.light' : 'transparent',
                bgcolor: isSelected ? 'primary.50' : count > 0 ? 'error.50' : 'transparent',
                '&:hover': { bgcolor: 'action.hover' },
                position: 'relative',
              }}
            >
              <Typography
                variant="caption"
                fontWeight={isToday ? 'bold' : 'normal'}
                color={isSelected ? 'primary.main' : 'text.primary'}
              >
                {format(day, 'd')}
              </Typography>
              {count > 0 && (
                <Box
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    bgcolor: 'error.main',
                    mt: 0.25,
                  }}
                />
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export function AbsencesPage() {
  const navigate = useNavigate();
  const { role, schoolContext } = useAuthStore();

  const queryClient = useQueryClient();
  const { success: showSuccess, error: showError } = useToast();

  const [leftPanelTab, setLeftPanelTab] = useState<LeftPanelTab>('absence');
  const [logView, setLogView] = useState<LogView>('list');
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState<SortOption>('start_date_desc');
  const [page, setPage] = useState(1);
  const [highlightAbsenceId, setHighlightAbsenceId] = useState<string | null>(null);

  // Delete confirm dialog state
  const [deleteTarget, setDeleteTarget] = useState<AbsenceWithStatus | null>(null);

  // Cancel absence (has assignments) or hard-delete (pending, no assignments)
  const deleteMutation = useMutation({
    mutationFn: async (absence: AbsenceWithStatus) => {
      const canHardDelete = absence.status === 'pending' && absence.unassigned_periods_count === 0;
      if (canHardDelete) {
        await deleteAbsenceDirect(absence.id);
      } else {
        await cancelAbsence(absence.id);
      }
    },
    onSuccess: (_, absence) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.absences.all });
      const canHardDelete = absence.status === 'pending' && absence.unassigned_periods_count === 0;
      showSuccess(canHardDelete ? 'Absence deleted' : 'Absence cancelled');
      setDeleteTarget(null);
    },
    onError: (err: any) => {
      showError(err.message || 'Failed to delete absence');
      setDeleteTarget(null);
    },
  });

  // Calendar popover state
  const [calendarSelectedDay, setCalendarSelectedDay] = useState<string | null>(null);
  const [calendarDayAbsences, setCalendarDayAbsences] = useState<AbsenceWithStatus[]>([]);

  useEffect(() => {
    if (role === 'teacher') navigate('/dashboard');
  }, [role, navigate]);

  const handleAbsenceCreated = (absenceId: string) => {
    setHighlightAbsenceId(absenceId);
    setTimeout(() => setHighlightAbsenceId(null), 4000);
  };

  // Paginated list query
  const { data: absences, isLoading, error } = useQuery({
    queryKey: queryKeys.absences.list({
      school_id: schoolContext || '',
      status: 'all',
      search,
      sort_by: sortBy,
      page,
      page_size: PAGE_SIZE,
    }),
    queryFn: () =>
      getAbsences({
        school_id: schoolContext || '',
        status: 'all',
        search: search || undefined,
        sort_by: sortBy,
        page,
        page_size: PAGE_SIZE,
      }),
    enabled: !!schoolContext,
  });

  // All absences for calendar (unpaginated)
  const { data: allAbsences } = useQuery({
    queryKey: queryKeys.absences.list({ school_id: schoolContext || '', _calendar: true }),
    queryFn: () =>
      getAbsences({ school_id: schoolContext || '', status: 'all', page: 1, page_size: 500 }),
    enabled: !!schoolContext && logView === 'calendar',
  });

  useEffect(() => { setPage(1); }, [search, sortBy]);

  if (role === 'teacher') return null;

  const hasNextPage = (absences?.length ?? 0) === PAGE_SIZE;
  const totalPages = hasNextPage ? page + 1 : page;

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Absences &amp; Availability
      </Typography>
      <Typography variant="body1" color="text.secondary" paragraph>
        Record teacher absences, manage unavailability, and view the absence log
      </Typography>

      <Grid container spacing={3}>
        {/* Left Panel: Record Absence / Unavailability */}
        <Grid item xs={12} lg={4}>
          <Paper sx={{ p: 3 }}>
            <Tabs
              value={leftPanelTab}
              onChange={(_, v) => setLeftPanelTab(v as LeftPanelTab)}
              sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
            >
              <Tab value="absence" label="Record Absence" />
              <Tab value="unavailability" label="Unavailability" />
            </Tabs>

            {leftPanelTab === 'absence' && (
              <Box>
                <Typography variant="h6" gutterBottom>Record Absence</Typography>
                <AbsenceForm onSuccess={handleAbsenceCreated} />
              </Box>
            )}

            {leftPanelTab === 'unavailability' && (
              <Box>
                <Typography variant="h6" gutterBottom>Teacher Unavailability</Typography>
                <UnavailabilityTab />
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Right Panel: Absence Log */}
        <Grid item xs={12} lg={8}>
          <Paper sx={{ p: 3 }}>
            {/* Header row */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6">Absence Log</Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  size="small"
                  variant={logView === 'list' ? 'contained' : 'outlined'}
                  startIcon={<ListIcon />}
                  onClick={() => setLogView('list')}
                >
                  List
                </Button>
                <Button
                  size="small"
                  variant={logView === 'calendar' ? 'contained' : 'outlined'}
                  startIcon={<CalendarMonthIcon />}
                  onClick={() => setLogView('calendar')}
                >
                  Calendar
                </Button>
              </Box>
            </Box>

            {/* ── List View ── */}
            {logView === 'list' && (
              <Box>
                <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                  <TextField
                    size="small"
                    placeholder="Search by teacher name..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                    sx={{ flex: 1, minWidth: 200 }}
                  />
                  <FormControl size="small" sx={{ minWidth: 160 }}>
                    <InputLabel>Sort by</InputLabel>
                    <Select
                      value={sortBy}
                      label="Sort by"
                      onChange={(e) => setSortBy(e.target.value as SortOption)}
                    >
                      {SORT_OPTIONS.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>

                {isLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : error ? (
                  <Alert severity="error">Failed to load absences</Alert>
                ) : !absences?.length ? (
                  <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                    No absences recorded yet.
                  </Typography>
                ) : (
                  <>
                    <List disablePadding>
                      {absences.map((absence) => {
                        const statusConfig = ABSENCE_STATUS_CHIP_CONFIG[absence.status] ?? ABSENCE_STATUS_CHIP_CONFIG.pending;
                        const isHighlighted = absence.id === highlightAbsenceId;
                        return (
                          <ListItem
                            key={absence.id}
                            disablePadding
                            secondaryAction={
                              <Tooltip title={
                                absence.status === 'posted'
                                  ? 'Cannot delete a posted absence'
                                  : absence.status === 'pending'
                                  ? 'Delete absence'
                                  : 'Cancel absence'
                              }>
                                <span>
                                  <IconButton
                                    edge="end"
                                    size="small"
                                    color="error"
                                    disabled={absence.status === 'posted'}
                                    onClick={() => setDeleteTarget(absence)}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            }
                            sx={{
                              mb: 0.5,
                              borderRadius: 1,
                              border: '1px solid',
                              borderColor: isHighlighted ? 'warning.main' : 'divider',
                              bgcolor: isHighlighted ? 'warning.50' : 'background.paper',
                              transition: 'border-color 0.3s, background-color 0.3s',
                              px: 2,
                              py: 1,
                              pr: 6,
                            }}
                          >
                            <ListItemText
                              primary={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                  <Typography variant="subtitle2" fontWeight="medium">
                                    {absence.teacher?.name || 'Unknown Teacher'}
                                  </Typography>
                                  <Chip
                                    label={statusConfig.label}
                                    color={statusConfig.color}
                                    size="small"
                                  />
                                </Box>
                              }
                              secondary={
                                <Box component="span" sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                                  <Typography variant="body2" component="span" color="text.secondary">
                                    {format(parseISO(absence.start_date), 'MMM d, yyyy')}
                                    {absence.start_date !== absence.end_date &&
                                      ` – ${format(parseISO(absence.end_date), 'MMM d, yyyy')}`}
                                    {' · '}
                                    {absence.absence_type === 'full_day' ? 'Full Day' : 'Temporary Duty'}
                                  </Typography>
                                  {absence.reason && (
                                    <Typography variant="caption" component="span" color="text.secondary">
                                      Reason: {absence.reason}
                                    </Typography>
                                  )}
                                </Box>
                              }
                            />
                          </ListItem>
                        );
                      })}
                    </List>

                    {totalPages > 1 && (
                      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                        <Pagination
                          count={totalPages}
                          page={page}
                          onChange={(_, p) => setPage(p)}
                          color="primary"
                          size="small"
                        />
                      </Box>
                    )}
                  </>
                )}
              </Box>
            )}

            {/* ── Calendar View ── */}
            {logView === 'calendar' && (
              <Box>
                <MiniCalendar
                  absences={allAbsences || []}
                  onDayClick={(date, teachers) => {
                    setCalendarSelectedDay(date);
                    setCalendarDayAbsences(teachers);
                  }}
                />

                {calendarSelectedDay && (
                  <Box sx={{ mt: 3 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      {format(parseISO(calendarSelectedDay), 'EEEE, MMMM d, yyyy')}
                    </Typography>
                    {calendarDayAbsences.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">
                        No absences on this day.
                      </Typography>
                    ) : (
                      <List disablePadding>
                        {calendarDayAbsences.map((a) => {
                          const statusConfig = ABSENCE_STATUS_CHIP_CONFIG[a.status] ?? ABSENCE_STATUS_CHIP_CONFIG.pending;
                          return (
                            <ListItem
                              key={a.id}
                              disablePadding
                              sx={{ mb: 0.5, px: 2, py: 0.75, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
                            >
                              <ListItemText
                                primary={
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography variant="body2" fontWeight="medium">
                                      {a.teacher?.name || 'Unknown Teacher'}
                                    </Typography>
                                    <Chip label={statusConfig.label} color={statusConfig.color} size="small" />
                                  </Box>
                                }
                                secondary={a.absence_type === 'full_day' ? 'Full Day' : 'Temporary Duty'}
                              />
                            </ListItem>
                          );
                        })}
                      </List>
                    )}
                  </Box>
                )}
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Delete / Cancel confirmation */}
      <ConfirmDialog
        open={!!deleteTarget}
        title={
          deleteTarget?.status === 'pending'
            ? 'Delete Absence'
            : 'Cancel Absence'
        }
        message={
          deleteTarget
            ? deleteTarget.status === 'pending'
              ? `Delete the absence record for ${deleteTarget.teacher?.name}? This cannot be undone.`
              : `Cancel the absence for ${deleteTarget.teacher?.name}? Any draft assignments will be discarded and substitutes will be notified.`
            : ''
        }
        confirmLabel={deleteTarget?.status === 'pending' ? 'Delete' : 'Cancel Absence'}
        severity={deleteTarget?.status === 'pending' ? 'error' : 'warning'}
        onConfirm={() => deleteTarget && deleteMutation.mutate(deleteTarget)}
        onCancel={() => setDeleteTarget(null)}
      />
    </Container>
  );
}
